/**
 * Lab web WebSocket klient pro backend multi-topic hub.
 *
 * Endpoint: `wss://<api>/api/ws/hub?token=<jwt>` (token z localStorage `ct-token`).
 *
 * Wire protocol (per backend `app/api/ws.py`):
 * - klient → server: `{action: "subscribe"|"unsubscribe"|"ping", topic?}`
 * - server → klient: `{event, topic?, ok?, error?, data?}`
 *
 * API (singleton `wsClient`):
 *   const sub = wsClient.subscribe('user:abc:notifications', (event) => { ... });
 *   sub.unsubscribe();
 *
 * Automaticky se připojí při prvním subscribe, drží connection dokud jsou
 * active subscriptions. Exponential backoff reconnect (1s → 30s). Po
 * reconnect se subscribuje znovu na všechny topiky. Mirror mobile
 * `src/lib/websocket.ts` pattern, ale v plain JS pro browser.
 *
 * Použití v React komponentě (přes `useWebSocket` hook):
 *   useWebSocket(`user:${userId}:notifications`, (event) => {
 *     if (event.event === 'notification.new') refetchNotifications();
 *   });
 */

const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10_000, 30_000];

class WSClient {
  constructor() {
    /** @type {WebSocket | null} */
    this.socket = null;
    /** @type {Map<string, Set<Function>>} */
    this.listeners = new Map();
    this.connectAttempt = 0;
    this.connecting = false;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this.reconnectTimer = null;
    this.intentionalClose = false;
  }

  /**
   * Subscribe na topic. Vrátí handle s `.unsubscribe()`. Bezpečné volat
   * opakovaně pro stejný topic (každý listener nezávislý).
   *
   * @param {string} topic
   * @param {(event: {event: string, topic?: string, data?: unknown}) => void} listener
   * @returns {{unsubscribe: () => void}}
   */
  subscribe(topic, listener) {
    let set = this.listeners.get(topic);
    if (!set) {
      set = new Set();
      this.listeners.set(topic, set);
    }
    set.add(listener);

    // Lazy connect — první subscriber otevře socket.
    this.ensureConnected().then(() => {
      this.send({ action: 'subscribe', topic });
    });

    return {
      unsubscribe: () => {
        const listeners = this.listeners.get(topic);
        if (!listeners) return;
        listeners.delete(listener);
        if (listeners.size === 0) {
          this.listeners.delete(topic);
          this.send({ action: 'unsubscribe', topic });
        }
        // Pokud žádný topic, zavřeme socket (úspora resources).
        if (this.listeners.size === 0) {
          this.disconnect();
        }
      },
    };
  }

  /** Vrátí current connection state (debug / UI indikátor). */
  getState() {
    if (!this.socket) return 'closed';
    if (this.socket.readyState === WebSocket.OPEN) return 'open';
    if (this.socket.readyState === WebSocket.CONNECTING) return 'connecting';
    return 'closed';
  }

  async ensureConnected() {
    if (this.socket?.readyState === WebSocket.OPEN) return;
    if (this.connecting) {
      // Počkej až se otevře.
      return new Promise((resolve) => {
        const id = setInterval(() => {
          if (!this.connecting) {
            clearInterval(id);
            resolve();
          }
        }, 50);
      });
    }
    await this.connect();
  }

  async connect() {
    this.connecting = true;
    this.intentionalClose = false;
    try {
      const token = localStorage.getItem('ct-token');
      const apiBaseUrl = process.env.REACT_APP_BACKEND_URL;
      if (!token || !apiBaseUrl) {
        console.warn('[ws] cannot connect — missing token or REACT_APP_BACKEND_URL');
        this.connecting = false;
        return;
      }
      // http(s)://host → ws(s)://host
      const wsBase = apiBaseUrl.replace(/^http/, 'ws').replace(/\/$/, '');
      const url = `${wsBase}/api/ws/hub?token=${encodeURIComponent(token)}`;

      this.socket = new WebSocket(url);
      this.socket.onopen = () => {
        this.connectAttempt = 0;
        this.connecting = false;
        // Re-subscribe na všechny topiky (po reconnect).
        for (const topic of this.listeners.keys()) {
          this.send({ action: 'subscribe', topic });
        }
      };
      this.socket.onmessage = (e) => this.handleMessage(e.data);
      this.socket.onerror = (err) => {
        console.warn('[ws] error', err);
      };
      this.socket.onclose = () => {
        this.connecting = false;
        this.socket = null;
        if (!this.intentionalClose && this.listeners.size > 0) {
          this.scheduleReconnect();
        }
      };
    } catch (err) {
      console.warn('[ws] connect failed', err);
      this.connecting = false;
      if (this.listeners.size > 0) {
        this.scheduleReconnect();
      }
    }
  }

  scheduleReconnect() {
    if (this.reconnectTimer) return;
    const delay =
      RECONNECT_DELAYS_MS[Math.min(this.connectAttempt, RECONNECT_DELAYS_MS.length - 1)];
    this.connectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /** @param {string} raw */
  handleMessage(raw) {
    let payload;
    try {
      payload = JSON.parse(raw);
    } catch (err) {
      console.warn('[ws] invalid JSON', raw, err);
      return;
    }
    const topic = payload.topic;
    if (!topic) {
      // System events (subscribed, pong, error) bez topicu — ignoruj.
      return;
    }
    const listeners = this.listeners.get(topic);
    if (!listeners || listeners.size === 0) return;
    for (const listener of listeners) {
      try {
        listener(payload);
      } catch (err) {
        console.warn('[ws] listener error', err);
      }
    }
  }

  /** @param {Record<string, unknown>} payload */
  send(payload) {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    try {
      this.socket.send(JSON.stringify(payload));
    } catch (err) {
      console.warn('[ws] send failed', err);
    }
  }

  disconnect() {
    this.intentionalClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      try {
        this.socket.close(1000, 'intentional');
      } catch {
        // ignore
      }
      this.socket = null;
    }
    this.connectAttempt = 0;
  }
}

export const wsClient = new WSClient();
